import { memo, useCallback, useMemo } from 'react';
import MojitoCore from 'mojito/core';
import MojitoPresentation from 'mojito/presentation';
import BannerCard from 'modules/event-cards/banner/index.js';
import MatchCard from 'modules/event-cards/match/index.js';
import OutrightCard from 'modules/event-cards/outright/index.js';
import FallbackCard from 'modules/event-cards/fallback/index.js';
import SelectionCard from 'modules/event-cards/selection/index.js';
import { useLoadingStatusMetric } from 'modules/performance/hooks';
import { useCardsPromotions } from './hooks';
import {
    usePromotions,
    usePromotionsLoadDone,
    usePromotionsPreload,
} from 'modules/promotions/hooks';
import EventCardsCarouselUtils from './utils.js';
import { isEmpty } from 'mojito/utils';
import EventCardsSkeleton from 'modules/event-cards/skeleton/index.js';
import EventCardsTypes from 'modules/event-cards/types.js';

const classUtils = MojitoCore.Base.classUtils;
const AnalyticsContextBuilder = MojitoCore.Presentation.AnalyticsContext.ContextBuilder;
const AnalyticsContextExtender = MojitoCore.Presentation.AnalyticsContext.ContextExtender;
const { Swiper, LoaderSuspense, EventCards, FlexPane, Text } = MojitoPresentation.Components;
const { CardLoader } = EventCards;
const { FALLBACK, MATCH, BANNER, OUTRIGHT, SELECTION } = EventCardsTypes.CARD_TYPE;
const { INPLAY, SPORT } = EventCardsTypes.FALLBACK_CONTENT_TYPE;

const createFallbackCard = type => ({
    id: type,
    type: FALLBACK,
    content: { type },
});

const fallbackCards = [INPLAY, SPORT].map(createFallbackCard);

const analyticsContext = new AnalyticsContextBuilder().withAnalyticsPath('EventCards').build();

export default function EventCardsCarousel(props) {
    const {
        mojitoTools: { config, instanceId },
        pageId,
        class: className,
    } = props;

    const { locationId, minCardsNumber, reportLoadingStatus } = config;

    const cardPromotions = usePromotions(locationId, pageId);
    const cards = useCardsPromotions(cardPromotions, config.priorityType.value);

    const promotionsLoaded = usePromotionsLoadDone();
    const [onPrepareSlidesToRender, eventsPreloaded, marketsPreloaded] = usePromotionsPreload(
        cardPromotions,
        promotionsLoaded,
        instanceId
    );

    const cardsToRender = useMemo(() => {
        return EventCardsCarouselUtils.enrichWithFallbackCards(
            cards.map(card => ({
                promotionText: card.content.text,
                ...card,
            })),
            minCardsNumber,
            fallbackCards
        );
    }, [cards, minCardsNumber]);

    const noContent = promotionsLoaded && isEmpty(cardsToRender);

    const performanceElementRef = useLoadingStatusMetric(
        promotionsLoaded || noContent,
        reportLoadingStatus ? instanceId : undefined,
        'EventCardsCarousel',
        noContent
    );

    if (noContent) {
        return null;
    }

    const isContentLoaded = promotionsLoaded && eventsPreloaded;

    return (
        <EventCardsContent
            {...props}
            class={className}
            isContentLoaded={isContentLoaded}
            preloadDone={eventsPreloaded && marketsPreloaded}
            cardsToRender={cardsToRender}
            performanceElementRef={performanceElementRef}
            onPrepareSlidesToRender={onPrepareSlidesToRender}
        />
    );
}

const EventCardsHeader = ({ headerText, config }) => {
    if (!headerText) {
        return null;
    }
    return (
        <FlexPane config={config.headerContainer}>
            <Text config={config.headerText}>{headerText}</Text>
        </FlexPane>
    );
};

// eslint-disable-next-line react/display-name
const EventCardsContent = memo(props => {
    const {
        mojitoTools: { config },
        isContentLoaded,
        performanceElementRef,
        class: className,
        headerText,
        cardsToRender,
    } = props;

    if (config.skeletonLoader.disableLoader && isEmpty(cardsToRender)) {
        return null;
    }

    const skeleton = (
        <EventCardsSkeleton config={config.eventCardsSkeleton} elementRef={performanceElementRef} />
    );

    return (
        <FlexPane
            config={config.container}
            class={classUtils.classNames('ta-EventCardsCarousel', className)}
        >
            <EventCardsHeader config={config} headerText={headerText} />
            <LoaderSuspense
                config={config.skeletonLoader}
                isContentPending={!isContentLoaded}
                loader={skeleton}
            >
                <CardsSwiper {...props} elementRef={performanceElementRef} />
            </LoaderSuspense>
        </FlexPane>
    );
});

const CardsSwiper = props => {
    const {
        mojitoTools: { config, appContext },
        onPrepareSlidesToRender,
        cardsToRender,
        elementRef,
        sportId,
        preloadDone,
    } = props;
    const { emitAnalytics } = appContext.analyticsEmitter;

    const onSlideChange = useCallback(
        () => emitAnalytics('eventCardsCarouselSwiped'),
        [emitAnalytics]
    );

    const cardRenderersByType = useMemo(
        () => createCardRenderer(sportId, preloadDone, config),
        [sportId, preloadDone, config]
    );

    const renderCard = card => {
        const renderFunc = cardRenderersByType[card.type];
        return renderFunc ? renderFunc(card) : undefined;
    };

    return (
        <AnalyticsContextExtender value={analyticsContext}>
            <Swiper
                config={config.swiper}
                elementRef={elementRef}
                onSlideChange={onSlideChange}
                onPrepareSlidesToRender={onPrepareSlidesToRender}
            >
                {cardsToRender.map(card => rendererCardSlide(card, renderCard))}
            </Swiper>
        </AnalyticsContextExtender>
    );
};

function rendererCardSlide(card, renderCard) {
    return function CardSlide(readyToLoad) {
        return (
            <CardLoader key={card.id} cardType={card.type} readyToLoad={readyToLoad}>
                {renderCard(card)}
            </CardLoader>
        );
    };
}

const createCardRenderer = (sportId, dataPreloadDone, config) => {
    const renderBannerCard = ({ id, content, altText, type }) => {
        const alternativeText = typeof altText !== 'undefined' ? altText : type;
        return (
            <BannerCard
                key={id}
                imgUrl={content.imgUrl}
                targetUrl={content.url}
                openInNewTab={content.openInNewTab}
                altText={alternativeText}
                cardBannerDetails={content.cardBannerDetails}
                locationId={config.locationId}
            />
        );
    };

    const renderFallbackCard = ({ id, content }) => {
        return (
            <FallbackCard
                key={id}
                type={content.type}
                sportId={sportId ?? config.fallbackSportId}
            />
        );
    };

    const renderOutrightCard = ({ id, content }) => {
        return (
            <OutrightCard
                key={id}
                eventId={content.eventId}
                marketId={content.marketId}
                shouldRequestData={dataPreloadDone}
                reportLoadingStatus={config.reportLoadingStatus}
            />
        );
    };

    const renderSelectionCard = ({ id, content, promotionText }) => {
        return (
            <SelectionCard
                key={id}
                eventId={content.eventId}
                marketId={content.marketId}
                selectionId={content.selectionId}
                marketName={content.marketName}
                selectionName={content.selectionName}
                promotionText={promotionText}
                shouldRequestData={dataPreloadDone}
                reportLoadingStatus={config.reportLoadingStatus}
            />
        );
    };

    const renderMatchCard = ({ id, content }) => {
        return (
            <MatchCard
                key={id}
                eventId={content.eventId}
                marketId={content.marketId}
                marketName={content.marketName}
                gameLine={content.gameLine}
                marketLines={content.marketLines}
                shouldRequestData={dataPreloadDone}
                reportLoadingStatus={config.reportLoadingStatus}
            />
        );
    };
    return {
        [MATCH]: renderMatchCard,
        [OUTRIGHT]: renderOutrightCard,
        [BANNER]: renderBannerCard,
        [FALLBACK]: renderFallbackCard,
        [SELECTION]: renderSelectionCard,
        ...config.cardRenderersByType,
    };
};
